import { IAlertProps, IBaseAlertProps } from "@components/alert/Alert.types";
import { WithBusyIndicator, withBusyIndicator } from "@components/busyIndicator/withBusyIndicator";
import { IGetValueArgs } from "@components/smart/FieldInfo";
import Summary from "@components/summary";
import SummaryItem from "@components/summary/SummaryItem";
import Wizard, { IStepChangeArgs } from "@components/wizard/Wizard";
import { withTheme } from "@hoc/withTheme";
import BindingContext, { createBindingContext, IEntity } from "@odata/BindingContext";
import { isODataError } from "@odata/Data.types";
import { IAssetEntity } from "@odata/GeneratedEntityTypes";
import { AssetItemTypeCode } from "@odata/GeneratedEnums";
import { WithOData, withOData } from "@odata/withOData";
import {
    getInActiveFYValidation,
    getSameOrLaterItemOfTypeValidation,
    getValidationResult
} from "@pages/asset/Asset.utils";
import { changeAssetPrice, IFixedAssetCustomData } from "@pages/asset/fixedAsset/FixedAsset.utils";
import {
    CHANGE_PRICE_FORM_ID,
    DatePath,
    getChangePriceDefFactory
} from "@pages/asset/fixedAsset/FixedAssetChangePriceDef";
import FixedAssetChangePriceFormView from "@pages/asset/fixedAsset/FixedAssetChangePriceFormView";
import { getDefinitionsFactory } from "@pages/asset/unorganizedAsset/UnorganizedAssetDef";
import UnorganizedAssetTableView from "@pages/asset/unorganizedAsset/UnorganizedAssetTableView";
import DialogPage from "@pages/DialogPage";
import { getFiscalYearByDate, isInFYorPeriod } from "@pages/fiscalYear/FiscalYear.utils";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { memoizeOne } from "@utils/memoizeOne";
import i18next from "i18next";
import React from "react";
import { Trans, WithTranslation, withTranslation } from "react-i18next";
import { ValidationError } from "yup";

import { AppContext, IAppContext } from "../../../contexts/appContext/AppContext.types";
import { Status } from "../../../enums";
import { TValue } from "../../../global.types";
import { PropsWithTheme } from "../../../theme";
import { formatCurrency } from "../../../types/Currency";
import DateType, { DisplayFormat } from "../../../types/Date";
import { getAlertFromError } from "../../../views/formView/Form.utils";
import { FormStorage } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import TableView from "../../../views/table";
import { VIEW_PADDING_VALUE } from "../../../views/View.styles";


interface IProps {
    rootStorage: FormStorage;
    onCancel: () => void;
    onConfirm?: (alert: IBaseAlertProps) => void;
    id: string;
}

interface IState {
    storage?: FormStorage;
    selectedRowsCount?: number;
}

enum ChangePriceSteps {
    Form = 0,
    Table = 1
}

type TFullProps = IProps & WithBusyIndicator & WithOData & PropsWithTheme & WithTranslation;

class ChangePrice extends React.Component<TFullProps, IState> {
    static contextType = AppContext;

    _formRef = React.createRef<FormViewForExtend<IFormViewProps<IEntity>>>();
    _tableViewRef = React.createRef<TableView>();

    def: IDefinition;

    constructor(props: TFullProps, context: IAppContext) {
        super(props);

        const storage = new FormStorage({
            id: CHANGE_PRICE_FORM_ID,
            oData: this.props.oData,
            t: this.props.t,
            theme: this.props.theme,
            context: context,
            refresh: () => {
                this.forceUpdate();
                this._formRef.current?.forceUpdate();
            }
        });

        this.state = { selectedRowsCount: 0, storage };
    }

    componentDidMount() {
        this.init();
    }

    init = async () => {
        if (this.getFormDefFactory().translationFiles) {
            // first load translations, then get definition
            await i18next.loadNamespaces(this.getFormDefFactory().translationFiles);
        }
        this.def = this.getFormDefFactory()(this.context);
        const bindingContext = createBindingContext(
                BindingContext.localContext("ChangePrice"),
                this.props.oData.metadata
        );

        await this.state.storage.init({
            definition: this.def.form,
            bindingContext,
            ignoreVariants: true
        });

        //this.props.onAfterLoad?.(this.storage);
        this.state.storage.refresh();
    };

    get date() {
        return this.state.storage.data.entity[DatePath];
    }

    getRootStorage = (): FormStorage<IAssetEntity, IFixedAssetCustomData> => {
        return this.props.rootStorage as FormStorage<IAssetEntity, IFixedAssetCustomData>;
    };

    getChangePriceType = (): AssetItemTypeCode => {
        return this.getRootStorage()?.getCustomData().AssetItemTypeCode;
    };

    getSteps = memoizeOne((): string[] => {
        const { rootStorage } = this.props;
        const assetItemType = this.getChangePriceType();
        const steps: string[] = [];
        [ChangePriceSteps.Form, ChangePriceSteps.Table].forEach(step =>
                steps.push(rootStorage.t(`FixedAsset:Unorganized.TitleChangePrice_${step}_${assetItemType}`)));

        return steps;
    });

    getTechnicalImprovementDateValidation = (value: TValue, args: IGetValueArgs): boolean | ValidationError => {
        if (this.getChangePriceType() === AssetItemTypeCode.TechnicalImprovement) {
            const storage = this.props.rootStorage;
            const asset = storage.data.entity as IAssetEntity;
            const FY = getFiscalYearByDate(storage.context, value as Date);
            if (isInFYorPeriod(FY, asset.DateFirstPutInUse)) {
                return new ValidationError(i18next.t("FixedAsset:Validation.TechnicalImprovementDate"), false, args.bindingContext.getPath(true));
            }
        }
        return true;
    };

    changePriceDateCustomValidator = (value: TValue, args: IGetValueArgs): boolean | ValidationError => {
        const argsWithAssetStorage = { ...args, storage: this.props.rootStorage };
        return getValidationResult(
                getInActiveFYValidation(value, args),
                getSameOrLaterItemOfTypeValidation(value, argsWithAssetStorage, [
                    AssetItemTypeCode.TechnicalImprovement,
                    AssetItemTypeCode.IncreaseOfPrice,
                    AssetItemTypeCode.ReductionOfPrice,
                    AssetItemTypeCode.PutInUse
                ]),
                this.getTechnicalImprovementDateValidation(value, args)
        );
    };


    handleChangeStep = (nextStep: number, currentStep: number): void => {
        if (nextStep === ChangePriceSteps.Form) {
            this.setState({ selectedRowsCount: 0 });
        }
    };

    /**
     * Confirm of change price editable window action, todo: ...
     */
    handleWizardConfirm = async (): Promise<void> => {
        const { rootStorage } = this.props;
        let alert: IAlertProps;
        const tableStorage = this._tableViewRef.current.props.storage;

        const assetId = rootStorage.data.entity.Id;
        const affectedRows = await tableStorage.tableAPI.getAffectedRows();
        const entryIds = affectedRows.map(row => row.bc.getKey());

        this.props.setBusy(true);
        try {
            const originalAsset = rootStorage.data.entity as IAssetEntity;
            const type = this.getChangePriceType();
            const data = await changeAssetPrice(type, this.date, entryIds, assetId);
            const price = formatCurrency(data.CalculatedPrice, data.CurrencyCode);
            const originalPrice = formatCurrency(originalAsset.CalculatedPrice, data.CurrencyCode);
            const title = i18next.t(`FixedAsset:ChangePrice.SuccessAlert_${type}`);

            alert = {
                status: Status.Success,
                title: i18next.t("Common:Validation.SuccessTitle"),
                hoverTitle: title,
                subTitle: (<>
                    {title}
                    <br/>
                    <Trans i18nKey={"FixedAsset:ChangePrice.SuccessAlertSubtitle"}
                           values={{ price, originalPrice }}
                           components={{
                               1: <strong/>
                           }}/>
                </>)
            };
        } catch (e) {
            if (isODataError(e)) {
                const assetPriceValidation = e._validationMessages?.find(message => message.code === "AssetValueCannotBeUpdatedLowPrice");
                if (assetPriceValidation) {
                    // we want to show this message in this particular case
                    // todo: how to solve this generally??
                    e._message = assetPriceValidation.message;
                }
            }
            alert = getAlertFromError(e);
        }

        this.props.setBusy(false);
        const isSuccess = alert.status === Status.Success;

        if (isSuccess) {
            this.props.onConfirm?.(alert);
        } else {
            // error sets the alert on tableView and keeps the editable window open
            tableStorage.setTableAlert(alert);
            tableStorage.refresh();
            this._tableViewRef.current?.forceUpdate();
        }
    };

    getFormDefFactory = memoizeOne((): IGetDefinition => {
        return getChangePriceDefFactory(this.changePriceDateCustomValidator);
    }, () => [this.getChangePriceType()]);

    getTableDefFactory = memoizeOne((): IGetDefinition => {
        return getDefinitionsFactory(this.date, this.props.id, true, this.getChangePriceType());
    }, () => [DateType.format(this.date, DisplayFormat.DateFull)]);

    checkStepValidity = async (args: IStepChangeArgs): Promise<boolean> => {
        const { nextStep, isComplete } = args;
        if (nextStep === ChangePriceSteps.Table) {
            // validate form before going to the table step
            const { storage } = this._formRef.current.props;
            if (await storage.validate(true)) {
                this._formRef.current.forceUpdate();
                return false; // prevent step change if validation fails
            }
            return true;
        }
        if (isComplete) {
            // can confirm only if there are selected rows in the table
            return this.state.selectedRowsCount > 0;
        }
        return true; // allow step change by default
    }

    handleActiveRowActionCountChange = (count: number): void => {
        this.setState({ selectedRowsCount: count });
    }

    renderForm(title: string, buttons: React.ReactElement): React.ReactElement {
        const { storage } = this.state;

        return (
                <FixedAssetChangePriceFormView title={title}
                                               buttons={buttons}
                                               storage={storage}
                                               ref={this._formRef}
                                               formProps={{
                                                   hideBreadcrumbs: true,
                                                   shouldHideVariant: true,
                                                   shouldHideAuditTrail: true,
                                                   withoutPadding: true,
                                                   buttonsOnBottom: true,
                                                   afterIconsContent: this.renderSummary(),
                                                   style: {
                                                       padding: `${VIEW_PADDING_VALUE}px ${VIEW_PADDING_VALUE}px 0`
                                                   }
                                               }}
                />
        );
    }

    renderTable(title: string, buttons: React.ReactElement): React.ReactElement {
        return (
                <DialogPage
                        rootStorage={this.props.rootStorage}
                        tableView={UnorganizedAssetTableView}
                        tableViewProps={{
                            ref: this._tableViewRef,
                            onActiveRowActionCountChange: this.handleActiveRowActionCountChange,
                            title, buttons, summary: this.renderSummary()
                        }}
                        getDef={this.getTableDefFactory()}/>
        );
    }

    renderSummary(): React.ReactElement {
        const { rootStorage } = this.props;
        return (
                <Summary>
                    <SummaryItem id={"NumberOurs"} value={rootStorage.data.entity.NumberOurs} />
                </Summary>
        );
    }

    render() {
        return (
                <Wizard steps={this.getSteps()}
                        canChangeStep={this.checkStepValidity}
                        onCancel={this.props.onCancel}
                        onConfirm={this.handleWizardConfirm}
                        onChangeStep={this.handleChangeStep}
                        isConfirmDisabled={!this.state.selectedRowsCount}>
                    {(currentStep: number, title: string, buttons: React.ReactElement) => {
                        switch (currentStep) {
                            case ChangePriceSteps.Form:
                                return this.renderForm(title, buttons);
                            case ChangePriceSteps.Table:
                                return this.renderTable(title, buttons);
                            default:
                                return null;
                        }
                    }}
                </Wizard>
        );
    }
}

export default withTranslation(["FixedAsset", "Common"])(withBusyIndicator()(withTheme(withOData(ChangePrice))));